import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import moment from "moment";
import cn from "clsx";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  handleUpdateFields,
  handleInitializeRoleId,
  getRiskList,
  handleUpdateFieldsRestrictions,
  getPlayerRiskRestrictions,
  getPlayerWageringList,
  PlayerItem,
} from "store/players/list/slice";
import { ModalPlayerInfoTabs } from "configs/types";

import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import SearchDropdown, {
  Item as SearchDropdownItem,
} from "components/dropdowns/SearchDropdown/SearchDropdown";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import NotifsTable from "./ListInfoTable";
import ListInfoWagering from "./ListInfoWagering";
import ResponsibleGamingSection, {
  ResponsibleGamingSaveHandler,
} from "./ResponsibleGamingSection";

import lockIcon from "assets/icons/unlock-fill.svg";
import getSymbolFromCurrency from "currency-symbol-map";
import ReadOnlyInput from "components/inputs/ReadOnlyInput/ReadOnlyInput";
import { useSearchParams } from "react-router-dom";
import { ImageUpload } from "components/imageUpload";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

import styles from "./ListInfo.module.scss";
import PlayerProfitability from "./PlayerProfitability";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { getRolesList } from "store/roles/slice";
import KycStatusChangeModal from "./KycStatusChangeModal";
import {
  getKycStatusLabel,
  KYC_STATUS_OPTIONS,
  normalizeKycStatus,
} from "./kycStatus";

interface Props {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  tabs: string[];
  activeTab: string;
  setActiveTab: (e: string) => void;
  onResponsibleGamingSaveHandlerChange?: (
    handler: ResponsibleGamingSaveHandler | null,
  ) => void;
  notificationsLimit: number;
  onNotificationsLimitChange: (limit: number) => void;
}

type TMajorUserRestriction = {
  key:
    | keyof Pick<PlayerItem, "blockUser" | "blockDeposit" | "blockBet">
    | "blockWithdrawal"
    | "blockSport";
  label: string;
  icon: string;
  blocked: boolean | "unknown";
};

const ListInfoTab: React.FC<Props> = ({
  fieldErrors,
  clearFieldError,
  notificationsLimit,
  onNotificationsLimitChange,
  tabs,
  activeTab,
  setActiveTab,
  onResponsibleGamingSaveHandlerChange,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const permissions = usePermissionHelper("/players/list");
  const canUpdate = permissions?.update ?? false;
  const [pendingKycStatus, setPendingKycStatus] = useState<ReturnType<
    typeof normalizeKycStatus
  > | null>(null);
  const isRiskFetched = useAppSelector((store) => store.players.isRiskFetched);
  const riskList = useAppSelector((store) => store.players.riskList);
  const rolesList = useAppSelector((store) => store.roles.list);
  const rolesListFetched = useAppSelector((store) => store.roles.isListFetched);
  const itemPayload = useAppSelector((store) => store.players.payload);
  const countries = useAppSelector((store) => store.settings.countries);
  const restrictionsInfo = useAppSelector(
    (store) => store.players.restrictionsInfo,
  );
  const isRestrictionsFetched = useAppSelector(
    (store) => store.players.isRestrictionsFetched,
  );
  const riskRestrictions = useAppSelector(
    (store) => store.players.riskRestrictions,
  );

  const playerRiskRestrictionsFetched = useAppSelector(
    (store) => store.players.isRiskRestrictionsFetched,
  );

  useEffect(() => {
    if (!isRiskFetched) {
      dispatch(getRiskList());
    }
    dispatch(getRolesList());
  }, []);

  const assignableRoles = rolesList.filter((role) => {
    const normalizedTitle = role.title.trim().toLowerCase();
    return (
      role.isSystem !== true && !["admin", "player"].includes(normalizedTitle)
    );
  });

  const currentRole = assignableRoles.find(
    (role) =>
      role.id === (itemPayload?.roleId ?? itemPayload?.role?.id) ||
      role.title.trim().toLowerCase() ===
        (itemPayload?.roleName ?? itemPayload?.role?.title)
          ?.trim()
          .toLowerCase(),
  );

  useEffect(() => {
    if (!itemPayload?.roleId && currentRole) {
      dispatch(handleInitializeRoleId(currentRole.id));
    }
  }, [currentRole?.id, itemPayload?.roleId, dispatch]);

  useEffect(() => {
    if (entityId) {
      dispatch(getPlayerRiskRestrictions(entityId));
    }
  }, [dispatch, entityId]);

  const handleData = (key: string, value: string | boolean | number) => {
    // @ts-ignore
    dispatch(handleUpdateFields({ [key]: value }));
  };
  const handleDataRestrictions = (
    key: string,
    value: string | boolean | number,
  ) => {
    clearFieldError(key);
    // @ts-ignore
    dispatch(handleUpdateFieldsRestrictions({ [key]: value }));
  };

  useEffect(() => {
    if (
      riskList.length > 0 &&
      itemPayload?.riskStatus &&
      typeof itemPayload?.riskStatus === "string"
    ) {
      const normalizedRiskStatus = itemPayload.riskStatus.trim().toLowerCase();
      const matchingRiskId = riskList.find(
        (risk) => risk.title.trim().toLowerCase() === normalizedRiskStatus,
      )?.id;

      if (matchingRiskId !== undefined) {
        handleData("riskStatus", matchingRiskId);
      }
    }
  }, [riskList, itemPayload?.riskStatus]);

  const playerWithdrawalRiskRule = riskRestrictions?.items.find(
    (r) => r.restrictionType === "withdraw",
  );

  const playerWithdrawalRisk = !playerRiskRestrictionsFetched
    ? "unknown"
    : playerWithdrawalRiskRule?.isBlocked || false
      ? true
      : false;

  const playerSportRiskRule = riskRestrictions?.items.find(
    (r) => r.restrictionType === "sport",
  );

  const playerSportRisk = !playerRiskRestrictionsFetched
    ? "unknown"
    : !!playerSportRiskRule?.isBlocked;

  const majorUserRestrictions: TMajorUserRestriction[] = [
    {
      key: "blockUser",
      label: "user",
      icon: "/sidebar/player.svg",
      blocked: !!itemPayload?.blockUser,
    },
    {
      key: "blockDeposit",
      label: "deposit",
      icon: "/sidebar/deposits.svg",
      blocked: !!itemPayload?.blockDeposit,
    },
    {
      key: "blockBet",
      label: "casino",
      icon: "/sidebar/casino.svg",
      blocked: !!itemPayload?.blockBet,
    },
    {
      key: "blockWithdrawal",
      label: "withdrawal",
      icon: "/sidebar/withdrawals.svg",
      blocked: playerWithdrawalRisk,
    },
    {
      key: "blockSport",
      label: "sport",
      icon: "/sidebar/sport.svg",
      blocked: playerSportRisk,
    },
  ];

  const countryItems: SearchDropdownItem[] = countries.map((country) => ({
    value: country.code,
    label: country.name,
    icon: `https://flagpedia.net/data/flags/icon/16x12/${country.code.toLowerCase()}.png`,
  }));

  const selectedCountry = countryItems.find(
    (country) => country.value === itemPayload?.country,
  );
  const currentKycStatus = normalizeKycStatus(itemPayload?.verificationStatus);

  const renderTab = () => {
    if (activeTab === ModalPlayerInfoTabs.main) {
      return (
        <div className="player-main-form-wrapper">
          <div className="player-form-box">
            <div className="player-form-box-item row">
              <div className="flex-1">
                <InnerInput
                  value={itemPayload?.id?.toString() ?? ""}
                  placeholder="ID"
                  label="ID"
                  type="text"
                  id="player-id"
                  onChange={(e) => handleData("id", e)}
                  disabled={true}
                  icon={lockIcon}
                />
              </div>
              <div className={`flex-1 ${styles.countrySelect}`}>
                <label>{t("country")}</label>
                <SearchDropdown
                  label={t("selectCountry")}
                  items={countryItems}
                  defaultValues={selectedCountry ? [selectedCountry] : []}
                  mode="single"
                  isClearable={false}
                  keepSelectedInList
                  disabled={!canUpdate}
                  onSelect={(country) =>
                    handleData("country", String(country.value))
                  }
                />
              </div>
              <div className="d-flex j-c-space-between flex-1">
                <div>
                  <div className="inner-input-wrapper">
                    <label>{t("currency")}</label>
                  </div>
                  <div className="currency-tab-item mt-12">
                    <div className="currency">
                      {getSymbolFromCurrency(itemPayload?.currency ?? "") ?? ""}{" "}
                    </div>
                    {itemPayload?.currency}
                  </div>
                </div>
                <div>
                  <div className="inner-input-wrapper">
                    <label>{t("locale")}</label>
                  </div>
                  <div className="locales-wrapper mt-14">
                    {itemPayload?.locale && (
                      <img
                        src={`https://flagpedia.net/data/flags/icon/16x12/${itemPayload?.locale.split("-")[1]?.toLowerCase()}.png`}
                        alt=""
                      />
                    )}
                    {t(itemPayload?.locale ?? "")}
                  </div>
                </div>
              </div>
            </div>
            <div className="player-form-box-item row">
              <InnerInput
                value={itemPayload?.email ?? ""}
                placeholder={t("email")}
                label={t("email")}
                type="text"
                id="player-email"
                onChange={(e) => handleData("email", e)}
                disabled={true}
                icon={lockIcon}
              />
              <InnerInput
                value={itemPayload?.phone ?? ""}
                placeholder={t("phone")}
                label={t("phone")}
                type="phone"
                id="player-phone"
                onChange={(e) => handleData("phone", e)}
                disabled={!canUpdate}
              />
            </div>
            <div className="player-form-box-item row">
              <InnerInput
                value={itemPayload?.firstName ?? ""}
                placeholder="-"
                label={t("firstName")}
                type="text"
                id="player-firstName"
                onChange={(e) => handleData("firstName", e)}
                disabled={!canUpdate}
              />
              <InnerInput
                value={itemPayload?.lastName ?? ""}
                placeholder="-"
                label={t("lastName")}
                type="text"
                id="player-lastName"
                onChange={(e) => handleData("lastName", e)}
                disabled={!canUpdate}
              />
            </div>
            <div className="player-form-box-item row">
              <InnerInput
                value={itemPayload?.middleName ?? ""}
                placeholder="-"
                label={t("middlename")}
                type="text"
                id="player-middlename"
                onChange={(e) => handleData("middleName", e)}
                disabled={!canUpdate}
              />
              <InnerInput
                value={itemPayload?.nickname ?? ""}
                placeholder="-"
                label={t("nickname")}
                type="text"
                id="player-nickname"
                onChange={(e) => handleData("nickname", e)}
                disabled={!canUpdate}
              />
            </div>
            <div className="player-form-box-item row">
              <InnerInput
                value={itemPayload?.state ?? ""}
                placeholder="-"
                label={t("region")}
                type="text"
                id="player-state"
                onChange={(e) => handleData("state", e)}
                disabled={!canUpdate}
              />
              <InnerInput
                value={itemPayload?.city ?? ""}
                placeholder="-"
                label={t("city")}
                type="text"
                id="player-city"
                onChange={(e) => handleData("city", e)}
                disabled={!canUpdate}
              />
            </div>
            <div className="player-form-box-item row">
              <InnerInput
                value={itemPayload?.street ?? ""}
                placeholder="-"
                label={t("street")}
                type="text"
                id="player-street"
                onChange={(e) => handleData("street", e)}
                disabled={!canUpdate}
              />
              <InnerInput
                value={itemPayload?.house ?? ""}
                placeholder="-"
                label={t("house")}
                type="text"
                id="player-house"
                onChange={(e) => handleData("house", e)}
                disabled={!canUpdate}
              />
            </div>
            <div className="player-form-box-item row">
              <InnerInput
                value={itemPayload?.apartment ?? ""}
                placeholder="-"
                label={t("apartment")}
                type="text"
                id="player-apartment"
                onChange={(e) => handleData("apartment", e)}
                disabled={!canUpdate}
              />
              <InnerInput
                value={itemPayload?.postalCode ?? ""}
                placeholder="-"
                label={t("postcode")}
                type="text"
                id="player-postalCode"
                onChange={(e) => handleData("postalCode", e)}
                disabled={!canUpdate}
              />
            </div>
            <div className="player-form-box-item">
              <InnerInput
                value={itemPayload?.address ?? ""}
                placeholder="-"
                label={t("address")}
                type="text"
                id="player-address"
                onChange={(e) => handleData("address", e)}
                disabled={!canUpdate}
              />
            </div>
          </div>
          <div className={cn("player-form-box", styles.detailsColumn)}>
            <div className={styles.profileHeader}>
              <div className="player-avatar-wrapper">
                <ImageUpload
                  image=""
                  size="default"
                  isActive
                  onAddImage={() => {}}
                  className={styles.avatar}
                />
              </div>
              <PlayerProfitability key={entityId} userId={Number(entityId)} />
            </div>
            <div className="player-labels">
              <div
                className={`player-online-label ${itemPayload?.isOnline ? "online" : ""}`}
              >
                {itemPayload?.isOnline ? t("online") : t("offline")}
              </div>
              <div
                className={cn(
                  "player-kyc-label",
                  styles.kycStatus,
                  currentKycStatus,
                )}
              >
                <div className="lavel">KYC</div>
                {canUpdate ? (
                  <DefaultDropdown
                    value={currentKycStatus}
                    selectedLabel={getKycStatusLabel(currentKycStatus, t)}
                    useFixedMenu
                    options={KYC_STATUS_OPTIONS.map(({ value, labelKey }) => ({
                      value,
                      label: t(labelKey),
                    }))}
                    onSelect={(status) => {
                      if (status !== currentKycStatus) {
                        setPendingKycStatus(normalizeKycStatus(String(status)));
                      }
                    }}
                  />
                ) : (
                  <div className="text">
                    {getKycStatusLabel(currentKycStatus, t)}
                  </div>
                )}
              </div>
            </div>

            <div className="player-restrictions-preview">
              {majorUserRestrictions.map((r) => (
                <div className="restriction-preview-item" key={r.key}>
                  <div className="restriction-preview-left">
                    <img src={r.icon} alt={t(r.label)} />
                    <span title={t(r.label)}>{t(r.label)}</span>
                  </div>
                  <span
                    className={cn(
                      "badge",
                      r.blocked === "unknown"
                        ? "badge--neutral"
                        : r.blocked
                          ? "badge--blocked"
                          : "badge--low",
                    )}
                  >
                    {r.blocked === "unknown"
                      ? t("unknown")
                      : r.blocked
                        ? t("blocked")
                        : t("allowed")}
                  </span>
                </div>
              ))}
            </div>
            <p className={styles.manualRestrictionsNote}>
              {t("manualRestrictionsNote")}
            </p>

            <div className="player-form-box-item row">
              <InnerInput
                value={
                  itemPayload?.selectedRealBalance ??
                  itemPayload?.balanceReal?.toString() ??
                  ""
                }
                placeholder={t("balanceReal")}
                label={
                  itemPayload?.selectedRealBalanceCurrency
                    ? `${t("balanceReal")} (${itemPayload.selectedRealBalanceCurrency})`
                    : t("balanceReal")
                }
                type="text"
                id="player-balanceReal"
                onChange={(e) => handleData("balanceReal", e)}
                disabled={true}
                icon={lockIcon}
              />
              <InnerInput
                value={itemPayload?.balanceBonus?.toString() ?? ""}
                placeholder={t("balanceBonus")}
                label={t("balanceBonus")}
                type="number"
                id="player-balanceBonus"
                onChange={(e) => handleData("balanceBonus", e)}
                disabled={true}
                icon={lockIcon}
              />
            </div>
            <div
              className={cn(
                "player-form-box-item row",
                styles.birthdayRegistrationRow,
              )}
            >
              <div className={cn("date-picker-wrapper", styles.datePicker)}>
                <div className="header">
                  <label className="inner-input__label">{t("birthday")}</label>
                </div>

                <DatePicker
                  customInput={<ReadOnlyInput />}
                  dateFormat={"dd.MM.YYYY"}
                  showYearDropdown
                  disabled={!canUpdate}
                  selected={
                    itemPayload?.birthday
                      ? moment(
                          itemPayload?.birthday,
                          "DD.MM.YYYY",
                          true,
                        ).toDate()
                      : null
                  }
                  onChange={(date) =>
                    handleData("birthday", moment(date).format("DD.MM.YYYY"))
                  }
                />
              </div>
              <InnerInput
                value={itemPayload?.registeredAt ?? ""}
                placeholder="-"
                label={t("registrationDate")}
                type="text"
                id="player-registrationDate"
                onChange={() => {}}
                disabled
                icon={lockIcon}
              />
            </div>
            <div
              className={cn(
                "player-form-box-item row",
                styles.birthdayRegistrationRow,
              )}
            >
              <DefaultDropdown
                onSelect={(e) => {
                  handleData("riskStatus", e);
                  handleData("riskStatusChanged", true);
                }}
                isTop
                label={t("riskStatus")}
                placeholder={t("riskStatus")}
                value={itemPayload?.riskStatus}
                disabled={!canUpdate}
                options={riskList.map((el) => {
                  return {
                    value: el.id,
                    label: el.title,
                  };
                })}
              />
              <DefaultDropdown
                isTop
                label={t("role")}
                value={
                  itemPayload?.roleId ??
                  itemPayload?.role?.id ??
                  currentRole?.id
                }
                selectedLabel={
                  itemPayload?.roleName ?? itemPayload?.role?.title
                }
                placeholder={
                  itemPayload?.roleName ?? itemPayload?.role?.title ?? "-"
                }
                loading={!rolesListFetched}
                disabled={!canUpdate}
                onSelect={(roleId) => handleData("roleId", Number(roleId))}
                options={assignableRoles.map((role) => ({
                  value: role.id,
                  label: role.title,
                }))}
              />
            </div>
            <div className="player-form-box-item">
              <SwitchBtn
                disabled={!canUpdate}
                activeTxt={t("test")}
                isActive={!!itemPayload?.isTest}
                isRed
                handleClick={() => {
                  handleData("isTest", !itemPayload?.isTest);
                  clearFieldError("isTest");
                }}
                hasError={Boolean(fieldErrors?.["isTest"])}
              />
            </div>
          </div>
        </div>
      );
    }
    if (activeTab === ModalPlayerInfoTabs.restrictions) {
      if (!isRestrictionsFetched) return <ModalTabSkeleton />;
      return (
        <div className="player-main-form-wrapper restrictions">
          <div className="restrictions-limits-wrapper">
            <div className="player-form-box">
              <h3 className="restriction-group-title">{t("depositLimits")}</h3>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["dailyDepositLimit"])}
                  value={restrictionsInfo?.dailyDepositLimit?.toString() ?? ""}
                  placeholder={t("dailyDepositLimit")}
                  label={t("daily")}
                  type="number"
                  id="player-dailyDepositLimit"
                  onChange={(e) =>
                    handleDataRestrictions("dailyDepositLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["weeklyDepositLimit"])}
                  value={restrictionsInfo?.weeklyDepositLimit?.toString() ?? ""}
                  placeholder={t("weeklyDepositLimit")}
                  label={t("weekly")}
                  type="number"
                  id="player-weeklyDepositLimit"
                  onChange={(e) =>
                    handleDataRestrictions("weeklyDepositLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["monthlyDepositLimit"])}
                  value={
                    restrictionsInfo?.monthlyDepositLimit?.toString() ?? ""
                  }
                  placeholder={t("monthlyDepositLimit")}
                  label={t("monthly")}
                  type="number"
                  id="player-monthlyDepositLimit"
                  onChange={(e) =>
                    handleDataRestrictions("monthlyDepositLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["yearlyDepositLimit"])}
                  value={restrictionsInfo?.yearlyDepositLimit?.toString() ?? ""}
                  placeholder={t("yearlyDepositLimit")}
                  label={t("yearly")}
                  type="number"
                  id="player-yearlyDepositLimit"
                  onChange={(e) =>
                    handleDataRestrictions("yearlyDepositLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
            </div>
            <div className="player-form-box">
              <h3 className="restriction-group-title">
                {t("withdrawalLimits")}
              </h3>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["dailyWithdrawalLimit"])}
                  value={
                    restrictionsInfo?.dailyWithdrawalLimit?.toString() ?? ""
                  }
                  placeholder={t("dailyWithdrawalLimit")}
                  label={t("daily")}
                  type="number"
                  id="player-dailyWithdrawalLimit"
                  onChange={(e) =>
                    handleDataRestrictions("dailyWithdrawalLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["weeklyWithdrawalLimit"])}
                  value={
                    restrictionsInfo?.weeklyWithdrawalLimit?.toString() ?? ""
                  }
                  placeholder={t("weeklyWithdrawalLimit")}
                  label={t("weekly")}
                  type="number"
                  id="player-weeklyWithdrawalLimit"
                  onChange={(e) =>
                    handleDataRestrictions("weeklyWithdrawalLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["monthlyWithdrawalLimit"])}
                  value={
                    restrictionsInfo?.monthlyWithdrawalLimit?.toString() ?? ""
                  }
                  placeholder={t("monthlyWithdrawalLimit")}
                  label={t("monthly")}
                  type="number"
                  id="player-monthlyWithdrawalLimit"
                  onChange={(e) =>
                    handleDataRestrictions("monthlyWithdrawalLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
              <div className="player-form-box-item">
                <InnerInput
                  hasError={Boolean(fieldErrors?.["yearlyWithdrawalLimit"])}
                  value={
                    restrictionsInfo?.yearlyWithdrawalLimit?.toString() ?? ""
                  }
                  placeholder={t("yearlyWithdrawalLimit")}
                  label={t("yearly")}
                  type="number"
                  id="player-yearlyWithdrawalLimit"
                  onChange={(e) =>
                    handleDataRestrictions("yearlyWithdrawalLimit", e)
                  }
                  disabled={!canUpdate}
                />
              </div>
            </div>
          </div>
          <ResponsibleGamingSection
            disabled={!canUpdate}
            onSaveHandlerChange={onResponsibleGamingSaveHandlerChange}
          />
        </div>
      );
    }
    if (activeTab === ModalPlayerInfoTabs.notifications) {
      return (
        <NotifsTable
          limit={notificationsLimit}
          onLimitChange={onNotificationsLimitChange}
        />
      );
    }
    if (activeTab === ModalPlayerInfoTabs.wagering) {
      return <ListInfoWagering />;
    }

    return <div className="player-main-form-wrapper"></div>;
  };

  return (
    <div className="player-content-wrapper">
      <div className="game-modal-tabs small">
        {tabs.map((el) => (
          <div
            className={`tab-item ${activeTab === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setActiveTab(el);
            }}
          >
            {t(el)}
          </div>
        ))}
      </div>
      {renderTab()}
      {pendingKycStatus && itemPayload?.id && (
        <KycStatusChangeModal
          userId={itemPayload.id}
          currentStatus={currentKycStatus}
          nextStatus={pendingKycStatus}
          onClose={() => setPendingKycStatus(null)}
        />
      )}
    </div>
  );
};

export default ListInfoTab;
